import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../styles/InvoiceList.css";
import OrderDetails from '../services/OrderDetails';
import { useLanguage } from './../common/LanguageContext';
import axios from "axios";
import Swal from "sweetalert2";

const apiUrl = process.env.REACT_APP_API_URL;

const InvoiceList = ({ serviceName }) => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [downloading, setDownloading] = useState({}); // Track downloading state per invoice
  const { language } = useLanguage();
  useEffect(() => {
  const fetchInvoices = async () => {
    try {
      const resData = await OrderDetails.orderDetails(`${language}`, serviceName);

      const formattedData = resData.map((data) => {
        // Map backend orderState to friendly status
        let status = "Pending";
        if (data.orderState) {
          switch (data.orderState.toUpperCase()) {
            case "CONFIRMED":
              status = "Paid";
              break;
            case "CANCELLED":
              status = "Cancelled";
              break;
            case "PENDING_FOR_INVOICE":
            case "PENDING_IRN":
            case "ORDER_REQUEST_PENDING_FOR_APPROVAL_FROM_ERP":
            case "ORDER_REQUEST_PENDING_FOR_APPROVAL_FROM_IRN":
              status = "Pending";
              break;
            default:
              status = data.orderState; // fallback to raw value
          }
        }

        return {
          planKey: data.orderInfo.PLAN_NAME,
          date: data.orderDate,
          status: status.toLowerCase(), // keep lowercase for CSS styling
          amount: data.orderAmount,
          id: data.invoiceNumber,
          transactionId: data.transactionId,
        };
      });

      setInvoices(formattedData);
    } catch (error) {
      console.error("Error fetching order History Details:", error);
    }
  };

  fetchInvoices();
}, [language, serviceName]);

const handleDownload = async (txnId) => {
    // Prevent multiple simultaneous downloads
    if (downloading[txnId]) {
      return;
    }

    setDownloading(prev => ({ ...prev, [txnId]: true }));

    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get(`${apiUrl}/api/invoice/download/${txnId}/file`, {
        responseType: "blob", // important for binary data
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 30000, // 30 second timeout
      });

      // Check if response is actually a PDF
      if (response.data.size === 0) {
        throw new Error("Empty file received");
      }

      // Check content type
      const contentType = response.headers['content-type'];
      if (contentType && !contentType.includes('application/pdf')) {
        // If it's an error response, try to parse it
        const text = await response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || "Failed to download invoice");
        } catch (e) {
          throw new Error("Invalid file format received");
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Get filename from response headers or use transaction ID
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${txnId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Download Successful",
        text: "Invoice downloaded successfully",
        timer: 2000,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error("Download failed:", error);
      
      let errorMessage = "Failed to download invoice. Please try again.";
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 404) {
          errorMessage = "Invoice not found. Please contact support.";
        } else if (error.response.status === 401) {
          errorMessage = "Authentication failed. Please login again.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = `Download failed: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    } finally {
      setDownloading(prev => {
        const newState = { ...prev };
        delete newState[txnId];
        return newState;
      });
    }
  };


  return (
    <div className="invoice-container">
      <div className="invoice-header-wrapper">
        <div className="invoice-header">
          <div className="icon-wrapper">
            <i className="fas fa-file-alt"></i>
          </div>
          <h1 className="title">{t("my_invoices_label")}</h1>
        </div>
        <p className="invoice-summary">
          {t("total_invoices_label")} <span>{invoices.length}</span> {t("invoices_to_download")}
        </p>
      </div>
      <div className="invoice-table-wrapper">
        <table className="invoice-table">
          <thead>
            <tr>
              <th>{t("invoice_number_label")}</th>
              <th>{t("transaction_id_label")}</th>
              <th>{t("billing_date_label")}</th>
              <th>{t("plan_label")}</th>
              <th>{t("amount_label")}</th>
              <th>{t("status_label")}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice, index) => (
              <tr key={invoice.id || index}>
                <td data-label={t("invoice_number_label")}>
                  {invoice.id ? invoice.id : ""}
                </td>
                <td data-label={t("transaction_id_label")}>
                  #{invoice.transactionId}
                </td>
                <td data-label={t("billing_date_label")}>
                  {invoice.date}
                </td>
                <td data-label={t("plan_label")} style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: '150px'
                }} title={t(invoice.planKey)}>
                  {t(invoice.planKey)}
                </td>
                <td data-label={t("amount_label")}>
                  {Number(invoice.amount).toLocaleString()}
                </td>
                <td data-label={t("status_label")}>
                  {invoice.status === "paid" ? (
                    <button
                      className="invoice-download-btn"
                      onClick={() => handleDownload(invoice.transactionId)}
                      disabled={downloading[invoice.transactionId]}
                      style={{
                        opacity: downloading[invoice.transactionId] ? 0.6 : 1,
                        cursor: downloading[invoice.transactionId] ? "not-allowed" : "pointer"
                      }}
                    >
                      {downloading[invoice.transactionId] ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> {t("downloading_label") || "Downloading..."}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-download"></i> {t("download_label")}
                        </>
                      )}
                    </button>
                  ) : (
                    <span className="invoice-status-pending">{t("pending_status_label")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceList;